import {Meta} from '@rdcomponents/meta/meta';
import {useQueryClient} from '@tanstack/react-query';
import {Empty} from 'antd';
import {useEffect, useRef, useState} from 'react';

import {appRoute} from '../../../app-route';
import {InfiniteBrandSelector} from '../../../component/brand-select/infinite-brand-selector';
import {BreadCrumbs} from '../../../layout/bread-crumbs/bread-crumbs';
import {Page} from '../../../layout/page/page';
import {MainPageContainer} from '../../../layout/page-card/main-page-card';
import {PageCard} from '../../../layout/page-card/page-card';
import {Spinner} from '../../../layout/spinner/spinner';
import {Locale} from '../../../provider/locale/locale';
import {useLocale} from '../../../provider/locale/locale-hook';
import {isAiMonitoringRunTerminal} from '../../../service/ai-brand-monitoring/ai-brand-monitoring-const';
import {useAiMonitoringConfig, useAiMonitoringRun} from '../../../service/ai-brand-monitoring/ai-brand-monitoring-hook';
import {AiMonitoringConfigType} from '../../../service/ai-brand-monitoring/ai-brand-monitoring-type';
import {useUrl} from '../../../util/url-hook/url-hook';

import {DashboardView} from './components/dashboard/dashboard-view';
import {OnboardingView} from './components/onboarding/onboarding-view';
import * as styles from './ai-brand-monitoring.scss';

type QueryMapType = {
    brandId: string;
};

function isOnboardingState(config: AiMonitoringConfigType): boolean {
    return !config.enabled || config.prompts.length === 0;
}

export function AiBrandMonitoring(): JSX.Element {
    const {getLocalizedString} = useLocale();
    const {getQuery, setQuery} = useUrl<QueryMapType>();

    const brandIdQuery = getQuery('brandId');
    const parsedBrandId = brandIdQuery ? Number.parseInt(brandIdQuery, 10) : null;
    const brandId = typeof parsedBrandId === 'number' && Number.isSafeInteger(parsedBrandId) ? parsedBrandId : null;

    // The "current run" the dashboard is scoped to. A freshly started run is asynchronous
    // (queued/running): we store its id, poll it via useAiMonitoringRun, and refetch the
    // run-scoped queries once it reaches a terminal status (effect below).
    const [currentRunId, setCurrentRunId] = useState<string | null>(null);
    const queryClient = useQueryClient();
    const completedRunRef = useRef<string | null>(null);

    const {data: config, isLoading: isConfigLoading} = useAiMonitoringConfig(brandId);
    const {data: run} = useAiMonitoringRun(brandId, currentRunId);

    // When a run finishes, refresh everything scoped to it so the dashboard fills in. Guarded
    // by a ref so it runs once per completed run.
    useEffect(() => {
        if (!brandId || !currentRunId || !run || !isAiMonitoringRunTerminal(run.status)) {
            return;
        }

        if (completedRunRef.current === currentRunId) {
            return;
        }

        completedRunRef.current = currentRunId;
        queryClient.invalidateQueries(['ai-monitoring-analysis', brandId]);
        queryClient.invalidateQueries(['ai-monitoring-scores', brandId]);
        queryClient.invalidateQueries(['ai-monitoring-answers', brandId]);
        queryClient.invalidateQueries(['ai-monitoring-recommendations', brandId]);
    }, [brandId, currentRunId, run, queryClient]);

    function handleBrandChange(newBrandId: number): void {
        setQuery({brandId: String(newBrandId)});
        setCurrentRunId(null);
        completedRunRef.current = null;
    }

    function renderBody(): JSX.Element | null {
        if (brandId === null) {
            return (
                <PageCard>
                    <Empty description={getLocalizedString('AI_BRAND_MONITORING__SELECT_BRAND_PROMPT')} />
                </PageCard>
            );
        }

        if (isConfigLoading || !config) {
            return (
                <PageCard>
                    <Spinner />
                </PageCard>
            );
        }

        if (isOnboardingState(config)) {
            return <OnboardingView brandId={brandId} config={config} />;
        }

        return (
            <DashboardView
                brandId={brandId}
                config={config}
                currentRunId={currentRunId}
                onRunFinished={setCurrentRunId}
                run={run}
            />
        );
    }

    return (
        <Page renderContainer={(pageContent) => <MainPageContainer>{pageContent}</MainPageContainer>}>
            <Meta title={getLocalizedString('AI_BRAND_MONITORING__TITLE')} />
            <PageCard>
                <BreadCrumbs
                    list={[
                        {
                            path: appRoute.aiBrandMonitoring.path,
                            titleLangKey: 'AI_BRAND_MONITORING__TITLE',
                        },
                    ]}
                />

                <h1>
                    <Locale stringKey="AI_BRAND_MONITORING__TITLE" />
                </h1>

                <div className={styles.ai_brand_monitoring__brand_select}>
                    <div className={styles.ai_brand_monitoring__brand_select_label}>
                        <Locale stringKey="AI_BRAND_MONITORING__BRAND_SELECT__LABEL" />
                    </div>
                    <InfiniteBrandSelector
                        className={styles.ai_brand_monitoring__brand_select_input}
                        onChange={handleBrandChange}
                        value={brandId}
                    />
                </div>
            </PageCard>

            {renderBody()}
        </Page>
    );
}
